import { FC, useEffect, useCallback } from 'react'
import {
  RouteObject,
  useLocation,
  useRoutes,
  matchRoutes,
  useNavigate,
} from 'react-router-dom'
import Box from '@mui/material/Box'
import Footer from './components/Footer'
import Header from './components/Header'
import useGlobalContext from '@context/GlobalContext/useGlobalContext'
import analytics from './analytics'
import routesConfig from './route'
import { getTagByPathname } from './route/findRouteConfig'
import pageTitle from '@util/pageTitle'
import { OMRouteConfig } from './route/typing'
import localStore from '@service/local-store'
import useSurveyModal from '@context/SurveyModalContext/useSurveyModal'
import AdobeInitiator from '@analytics/adobe/AdobeInitiator'
import AnalyticsTracker from '@analytics/adobe/AnalyticsTracker'
import { getClientIpInfo } from './components/Contact/api'
import AdminFooter from '@admin/Footer'
import {
  extractOrganizationIdFromUrl,
  REFERRAL_STORAGE_KEYS,
  ReferredOrganization,
} from '@util/urlFilterParams'
import { validateOrganization } from '@patient/api/api'
import useUser from '@context/UserContext/useUser'
import { isAnySurveyCompleted } from '@util/surveyState'

const { trackPage } = analytics

const findRoute = (pathname: string, routes: OMRouteConfig[]) => {
  const matchedRoutes = matchRoutes(routes as any, pathname)
  return matchedRoutes
    ? matchedRoutes.map(({ route }) => route).find((route) => route.path)
    : []
}

const OptumMatch: FC = () => {
  const { pageHeight } = useGlobalContext()
  const location = useLocation()
  const routes = useRoutes(routesConfig as RouteObject[])
  const { setSurveyModalStatus } = useSurveyModal()
  const navigate = useNavigate()
  const { user } = useUser()

  const updatePageTitle = useCallback((route: any) => {
    const defaultTitle = 'Optum Match'
    const defaultLongTitle = 'Optum Match - A unique approach to finding care.'
    const defaultDescription =
      'A value-based care application that facilitates connections with clinicians you relate to, ensuring mutual understanding in healthcare administration.'

    let title = defaultTitle
    let longTitle = defaultLongTitle
    let description = defaultDescription

    if (route) {
      const ptc = JSON.parse(JSON.stringify(route))
      if (ptc.pageTitlesConfig) {
        const pageDetails = ptc.pageTitlesConfig
        title = pageDetails.shortTitle
        longTitle = pageDetails.longTitle
        description = pageDetails.description
      }
    }

    pageTitle(title, longTitle, description)
  }, [])

  const isInSurveyModal = useCallback(() => {
    const surveyState = localStore.get('surveyState')
    if (location.pathname === '/') return false

    const isSurveyEntryPath =
      location.pathname.includes('/begin-search') ||
      location.pathname.includes('/survey-selection') ||
      location.pathname.includes('/disclaimer')

    if (location.pathname.includes('/survey/review')) {
      return (
        surveyState === 'Started' || !isAnySurveyCompleted(user.surveyState)
      )
    }

    const isInSurveyPath = location.pathname.includes('/survey/')
    return surveyState === 'Started' || isInSurveyPath || isSurveyEntryPath
  }, [location.pathname, user.surveyState])

  useEffect(() => {
    if (/\/{2,}/.test(location.pathname)) {
      const pathname = location.pathname.replace(/\/{2,}/g, '/')
      navigate(pathname, { replace: true })
    }
    window.scrollTo(0, 0)
    const route = findRoute(location.pathname, routesConfig)
    const legacyTag =
      getTagByPathname(routesConfig, location.pathname) || location.pathname
    trackPage(legacyTag)

    // Handle organization referral from URL parameter
    const handleOrganizationReferral = async () => {
      const organizationId = extractOrganizationIdFromUrl(location.search)
      if (!organizationId) return

      // Check if we already have this organization validated and not yet applied
      const existingReferral = localStore.get(
        REFERRAL_STORAGE_KEYS.ORGANIZATION
      ) as ReferredOrganization | null
      if (
        existingReferral?.id === organizationId &&
        existingReferral?.validated
      ) {
        // Already validated, no need to re-validate
        return
      }

      try {
        // Validate the organization via API
        const validationResult = await validateOrganization(organizationId)

        // Store validated organization data for filter pre-selection
        const referralData: ReferredOrganization = {
          id: validationResult.id,
          name: validationResult.name,
          validated: true,
          extractedAt: new Date().toISOString(),
          appliedAt: null, // Will be set when filter is applied (one-time use)
        }
        localStore.set(REFERRAL_STORAGE_KEYS.ORGANIZATION, referralData)

        // Also maintain legacy key for backward compatibility with existing login flow
        localStore.set(
          REFERRAL_STORAGE_KEYS.ORGANIZATION_LEGACY,
          organizationId
        )
      } catch (error) {
        // Clean up any stale referral data
        localStore.remove(REFERRAL_STORAGE_KEYS.ORGANIZATION)
      }
    }

    handleOrganizationReferral()
    updatePageTitle(route)

    if (location.pathname === '/') {
      localStore.set('isFirstLoad', false)
      localStore.set('surveyState', '')
      setSurveyModalStatus(false)
    }
    const fetchData = async () => {
      try {
        const responseData = await getClientIpInfo()
        localStore.set('ipAddress', responseData)
      } catch (error) {
        return error
      } finally {
        //setLoading(false)
      }
    }
    fetchData()
    /* eslint-disable-next-line */
  }, [location, updatePageTitle, setSurveyModalStatus])

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      sx={({ ues }) => ({
        background: ues.neutral['5'],
      })}
    >
      <Box
        maxWidth={isInSurveyModal() ? 'none' : 'xl'}
        sx={({ ues, shadows, palette }) => ({
          borderLeft: { xs: 'none', xl: `1px solid ${ues.neutral['30']}` },
          borderRight: { xs: 'none', xl: `1px solid ${ues.neutral['30']}` },
          background: palette.common.white,
          boxShadow: shadows[4],
          overflow: 'hidden',
        })}
      >
        <Box
          minWidth={{
            xs: '100vw',
            sm: '100vw',
            md: '100vw',
            lg: '100vw',
            xl: isInSurveyModal() ? '100vw' : '1440px',
          }}
        >
          <AdobeInitiator />
          <AnalyticsTracker />
          {!isInSurveyModal() && !location.pathname.includes('admin') ? (
            <Header />
          ) : null}
          <Box role="main" sx={{ minHeight: pageHeight }}>
            {routes}
          </Box>
        </Box>
        {!isInSurveyModal() ||
        !(
          location.pathname.includes('admin') ||
          location.pathname.includes('survey') ||
          location.pathname.includes('begin-search') ||
          location.pathname.includes('survey-selection') ||
          location.pathname.includes('disclaimer')
        ) ||
        location.pathname === '/' ? (
          !location.pathname.includes('admin') &&
          !location.pathname.startsWith('/lp/') ? (
            <Footer />
          ) : null
        ) : null}
        {location.pathname.includes('admin') ? (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <AdminFooter />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default OptumMatch
